import React, { ComponentType, useMemo } from 'react'
import { OptionProps, Options, GroupBase, PlaceholderProps } from 'react-select'
import styled, { StyledProps } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { ClearIndicatorStyled, CustomSelectStyles } from '../BasicSelect/styles'
import { SimpleSelect, SimpleSelectOption, SimpleSelectProps } from '../SimpleSelect/SimpleSelect'
import { Switch } from '../../controls/Switch/Switch'

// eslint-disable-next-line @typescript-eslint/no-empty-function,@typescript-eslint/no-unused-vars
const noop = () => {}

export const customStyles: CustomSelectStyles = {
  control: () => ({}),
  valueContainer: () => ({ display: 'none' }),
  indicatorsContainer: () => ({ display: 'none' }),
}

const OptionStyled = styled.div`
  display: grid;
  grid-template-columns: max-content auto;
  margin: 0;
  cursor: pointer;
  color: ${props => props.theme.color.onPrimaryDark};
  transition: 0.3s background-color;
  font-weight: 500;
  line-height: 20px;
  min-height: 40px;
  font-size: 14px;
  border-radius: 4px;
  padding: 10px 8px;
  box-sizing: border-box;
  align-items: start;

  &[data-disabled='true'] {
    pointer-events: none;

    span {
      opacity: 0.7;
    }

    &:hover {
      background-color: transparent;
    }
  }

  &:hover {
    background-color: ${props => props.theme.color.primary50};

    button[role='checkbox'] {
      border-color: ${props => props.theme.color.primary500};
    }
  }

  &[data-nested-level='0'] {
    font-weight: 700;
  }

  &[data-nested-level='1'] {
    font-weight: 600;
    margin-left: 12px;
  }

  &[data-nested-level='2'] {
    margin-left: 24px;
  }
`

const PlaceholderStyled = styled.div`
  font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, sans-serif;
  color: ${(props: StyledProps<{ all: boolean }>) => (props.all ? '#808080' : props.theme.color.onPrimaryDark)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: calc(100% - 40px);
  position: absolute;
  left: 10px;
`

const ClearButtonStyled = styled(ClearIndicatorStyled)`
  position: absolute;
  right: 30px;
  bottom: 11px;
`

const Wrapper = styled.div`
  position: relative;
  top: -8px;
  right: -14px;
`

export type SwitchSelectOption = SimpleSelectOption & {
  isParentSelected?: boolean
  nestedLevel?: '0' | '1' | '2'
  labelToRender?: React.ReactNode
}

export type SwitchSelectProps = SimpleSelectProps & {
  options: Options<SwitchSelectOption>
  selected?: string[]
  showingLabel?: string
  onChange: (id: string) => void
  onClear?: () => void
  noneSelected?: string
  isSearchable?: boolean
  singleSelected?: string
  onSearchChange?: (value: string) => void
  onMenuClose?: () => void
}

export const SwitchSelect: React.FC<SwitchSelectProps> = ({
  selected = [],
  showingLabel,
  onChange,
  options,
  onClear,
  singleSelected,
  noneSelected,
  ...props
}) => {
  const { t } = useTranslation('ui')
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    e.bubbles = false
    onClear && onClear()
  }

  const unselectedOptions = useMemo(() => options.filter(o => !selected.includes(o.id as string)), [options, selected])
  const selectedOptions = useMemo(() => options.filter(o => selected.includes(o.id as string)), [options, selected])

  const Option: ComponentType<OptionProps<SwitchSelectOption, boolean, GroupBase<SwitchSelectOption>> & {
    value: string
  }> = ({ value, data, label }) => {
    return (
      <OptionStyled
        onClick={() => onChange(value)}
        id={value}
        data-nested-level={data.nestedLevel || ''}
        data-disabled={data.isParentSelected}
      >
        <Switch
          on={!!(selected.includes(value) || data.isParentSelected)}
          onClick={noop}
          disabled={data.isParentSelected}
          size={'small'}
        />
        <span>{data.labelToRender || label}</span>
      </OptionStyled>
    )
  }
  const Placeholder: ComponentType<PlaceholderProps<SwitchSelectOption, boolean, GroupBase<SwitchSelectOption>>> = ({
    selectProps,
  }) => {
    return (
      <PlaceholderStyled all={selected.length === 0}>
        {selected.length > 0
          ? singleSelected || t('CheckboxSelect.multipleSelected', { items: selectProps.placeholder })
          : noneSelected
          ? noneSelected
          : t('CheckboxSelect.allSelected', { items: selectProps.placeholder })}
      </PlaceholderStyled>
    )
  }

  return (
    <Wrapper>
      <SimpleSelect
        {...props}
        onChange={noop}
        customStyles={customStyles}
        options={[
          ...unselectedOptions,
          {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            label: showingLabel,
            options: selectedOptions,
          },
        ]}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        components={{ Option, Placeholder }}
        filterOption={() => true}
        isSearchable={false}
        noneSelected={noneSelected}
        menuIsOpen
        minMenuHeight={800}
      />
      {!!selected.length && !!onClear && <ClearButtonStyled onClick={handleClear} />}
    </Wrapper>
  )
}
